import React, { useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, TextField, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Stack, Divider, Alert, CircularProgress
} from '@mui/material';
import { DateRange as DateRangeIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { profitAPI } from '../Api';
import PageHeader from '../components/PageHeader';
import StatsCard from '../components/StatsCard';

const ProfitReport = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
  });

  const handleFilterChange = (field) => (e) => {
    setFilters({ ...filters, [field]: e.target.value });
  };

  const fetchReport = async () => {
    if (!filters.start_date || !filters.end_date) {
      setError('Укажите даты периода');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await profitAPI.getReport(filters);
      if (res.data.success) {
        setData(res.data.data);
      } else {
        setError(res.data.message || 'Ошибка при загрузке отчёта');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Ошибка при загрузке отчёта');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value, currency = 'RUB') => {
    if (value === null || value === undefined) return '—';
    return Number(value).toLocaleString('ru-RU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + ` ${currency}`;
  };

  const setToday = () => {
    const today = new Date().toISOString().split('T')[0];
    setFilters({ start_date: today, end_date: today });
  };

  const setYesterday = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const dateStr = yesterday.toISOString().split('T')[0];
    setFilters({ start_date: dateStr, end_date: dateStr });
  };

  const setLast7Days = () => {
    const end = new Date().toISOString().split('T')[0];
    const start = new Date();
    start.setDate(start.getDate() - 6);
    const startStr = start.toISOString().split('T')[0];
    setFilters({ start_date: startStr, end_date: end });
  };

  const setLast30Days = () => {
    const end = new Date().toISOString().split('T')[0];
    const start = new Date();
    start.setDate(start.getDate() - 29);
    const startStr = start.toISOString().split('T')[0];
    setFilters({ start_date: startStr, end_date: end });
  };

  return (
    <Box>
      <PageHeader title="Отчёт по прибыли" />

      {/* Фильтры */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Дата начала"
                type="date"
                value={filters.start_date}
                onChange={handleFilterChange('start_date')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Дата окончания"
                type="date"
                value={filters.end_date}
                onChange={handleFilterChange('end_date')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Button variant="outlined" size="small" onClick={setToday}>
                  Сегодня
                </Button>
                <Button variant="outlined" size="small" onClick={setYesterday}>
                  Вчера
                </Button>
                <Button variant="outlined" size="small" onClick={setLast7Days}>
                  7 дней
                </Button>
                <Button variant="outlined" size="small" onClick={setLast30Days}>
                  30 дней
                </Button>
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={fetchReport}
                  disabled={loading}
                >
                  Сформировать
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box display="flex" justifyContent="center" sx={{ py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {data && !loading && (
        <>
          {/* Общая статистика */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCard
                title="Оплачено ордеров"
                value={data.totalOrders}
                icon={DateRangeIcon}
                color="primary"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCard
                title="Принято"
                value={formatCurrency(data.totalTotalAmount, 'RUB')}
                subtitle={formatCurrency(data.totalTotalAmountUsdt, 'USDT')}
                color="success"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCard
                title="Выплатить"
                value={formatCurrency(data.totalAmount, 'RUB')}
                subtitle={formatCurrency(data.totalAmountUsdt, 'USDT')}
                color="warning"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCard
                title="Общая прибыль"
                value={formatCurrency(data.totalProfit, 'RUB')}
                subtitle={formatCurrency(data.totalProfitUsdt, 'USDT')}
                color="info"
              />
            </Grid>
          </Grid>

          {/* Курс USDT */}
          {data.usdtRate > 0 && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Курс USDT: {formatCurrency(data.usdtRate, 'RUB')}
            </Alert>
          )}

          {/* Прибыль по категориям */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    🏪 Основные мерчанты (main)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Прибыль: {formatCurrency(data.totalMainProfit, 'RUB')} ({formatCurrency(data.totalMainProfitUsdt, 'USDT')})
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    🔗 Сторонние мерчанты (external)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Прибыль: {formatCurrency(data.totalExternalProfit, 'RUB')} ({formatCurrency(data.totalExternalProfitUsdt, 'USDT')})
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Таблица по основным мерчантам */}
          {data.mainGroups.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  🏪 Основные мерчанты (main)
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Группа</TableCell>
                        <TableCell>Прибыль</TableCell>
                        <TableCell>До amount</TableCell>
                        <TableCell>Ордеров</TableCell>
                        <TableCell>Комиссия</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.mainGroups.map((group) => (
                        <TableRow key={`${group.procId}-main`}>
                          <TableCell>
                            <Chip label={group.procId} size="small" color="primary" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatCurrency(group.profit, 'RUB')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatCurrency(group.profitUsdt, 'USDT')}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatCurrency(group.totalAmountAfterCommission, 'RUB')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatCurrency(group.totalAmountAfterCommissionUsdt, 'USDT')}
                            </Typography>
                          </TableCell>
                          <TableCell>{group.count}</TableCell>
                          <TableCell>{group.commission}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {/* Таблица по сторонним мерчантам */}
          {data.externalGroups.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  🔗 Сторонние мерчанты (external)
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Группа</TableCell>
                        <TableCell>Прибыль</TableCell>
                        <TableCell>До amount</TableCell>
                        <TableCell>Ордеров</TableCell>
                        <TableCell>Комиссия</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.externalGroups.map((group) => (
                        <TableRow key={`${group.procId}-external`}>
                          <TableCell>
                            <Chip label={group.procId} size="small" color="secondary" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatCurrency(group.profit, 'RUB')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatCurrency(group.profitUsdt, 'USDT')}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatCurrency(group.totalAmountAfterCommission, 'RUB')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatCurrency(group.totalAmountAfterCommissionUsdt, 'USDT')}
                            </Typography>
                          </TableCell>
                          <TableCell>{group.count}</TableCell>
                          <TableCell>{group.commission}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {/* Общий итог по группам */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📊 Общая прибыль по группам процессингов
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Группа</TableCell>
                      <TableCell>Прибыль</TableCell>
                      <TableCell>До amount</TableCell>
                      <TableCell>Ордеров</TableCell>
                      <TableCell>Комиссия</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.totalGroups.map((group) => (
                      <TableRow key={group.procId}>
                        <TableCell>
                          <Chip label={group.procId} size="small" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatCurrency(group.profit, 'RUB')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatCurrency(group.profitUsdt, 'USDT')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatCurrency(group.totalAmountAfterCommission, 'RUB')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatCurrency(group.totalAmountAfterCommissionUsdt, 'USDT')}
                          </Typography>
                        </TableCell>
                        <TableCell>{group.count}</TableCell>
                        <TableCell>{group.commission}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Программистам */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                👨‍💻 Программистам
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Формула: (прибыль main × 0.20) + (прибыль external × 0.50)
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    🏪 От основных
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(data.programmersMainShare, 'RUB')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatCurrency(data.programmersMainShareUsdt, 'USDT')}
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary">
                    ({formatCurrency(data.totalMainProfit, 'RUB')} × 0.20)
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    🔗 От сторонних
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(data.programmersExternalShare, 'RUB')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatCurrency(data.programmersExternalShareUsdt, 'USDT')}
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary">
                    ({formatCurrency(data.totalExternalProfit, 'RUB')} × 0.50)
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    💻 Итого программистам
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {formatCurrency(data.totalProgrammersShare, 'RUB')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatCurrency(data.totalProgrammersShareUsdt, 'USDT')}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default ProfitReport;
